import React from 'react';
import { Link, useInRouterContext, useLocation } from 'react-router-dom';
import { BookOpenIcon } from '@heroicons/react/24/outline';
import { track, EVENTS } from '../services/analytics';

/**
 * A pointer from a working surface to the paper that explains it.
 *
 * Founder, 2026-08-09: "I liked the way we pointed at this paper within the
 * product.. lets take the approach everywhere in the product where it makes
 * sense.. it keeps everything transparent to the users and in a way educates
 * the customers as well."
 *
 * The gap this closes is embarrassing and was invisible until we looked: 19
 * papers exist, all published and publicly indexed, and before this the app
 * linked to exactly ONE of them (the Universities page, the same evening).
 * Every explanation of how the product thinks had been written and then left
 * where only a search engine would find it.
 *
 * `label` is passed explicitly rather than read from the papers registry on
 * purpose. Importing that registry into a product page would pull all 19
 * papers' full body text into the page's bundle to render a nine-word link.
 * The cost of passing the label by hand is that a slug can rot — so
 * learnMoreLinks.test.jsx validates every slug against the registry at test
 * time, where it's free.
 *
 * `fromLabel` is the ORIGIN page's own title (handed down by ValueSpotlight).
 * It rides in link state so the paper can offer a way back to the page the
 * reader was actually working on — see PaperHero. Using the page's real title
 * beats a path->name lookup table, which would be a second place to maintain
 * page names and would drift the first time one was renamed.
 *
 * Every click is RESOURCE_OPENED {slug, from: 'page', page} (#5309): the
 * paper's own page view says a paper was read, this says which product page
 * sent the reader. It became the library's main door on 2026-10-24, when the
 * signed-in sidebar dropped Resources (founder, Desk request R-32).
 */

const trackOpen = (slug, page) => track(EVENTS.RESOURCE_OPENED, { slug, from: 'page', page });

const styles = (className) =>
    `inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-[#1A4D2E] hover:underline underline-offset-2 ${className}`;

const Inner = ({ label }) => (
    <>
        <BookOpenIcon className="h-4 w-4 shrink-0 opacity-70" aria-hidden="true" />
        {label}
        <span aria-hidden="true">→</span>
    </>
);

// Split out so the router hooks are only ever called when a Router exists —
// hooks can't be conditional, and useLocation THROWS outside one.
function RoutedLearnMore({ slug, label, fromLabel, className }) {
    const { pathname } = useLocation();
    return (
        <Link
            to={`/resources/${slug}`}
            state={{ from: pathname, fromLabel }}
            onClick={() => trackOpen(slug, pathname)}
            data-testid={`learn-more-${slug}`}
            className={styles(className)}
        >
            <Inner label={label} />
        </Link>
    );
}

export default function LearnMore({ slug, label, fromLabel, className = '' }) {
    // This lives inside ValueSpotlight, the shared page hero — so adding a
    // <Link> here silently imposed a Router requirement on every page that
    // uses it, and took down three FinancialAidComparison tests that render
    // the page without one. A header component that hard-crashes its host
    // depending on how it's mounted is a landmine; degrade to a plain anchor
    // instead. Inside the app there is always a Router, so the SPA navigation
    // path is the one that actually runs.
    if (!useInRouterContext()) {
        return (
            <a
                href={`/resources/${slug}`}
                onClick={() => trackOpen(slug, typeof window === 'undefined' ? null : window.location.pathname)}
                data-testid={`learn-more-${slug}`}
                className={styles(className)}
            >
                <Inner label={label} />
            </a>
        );
    }
    return <RoutedLearnMore slug={slug} label={label} fromLabel={fromLabel} className={className} />;
}
